import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { Brain, DollarSign, Gauge, Lightbulb, Plus, RefreshCw, Zap, Wand2, TrendingUp, Target, ChevronDown, FileText, Filter, Wallet } from "lucide-react";
import { useOrgRole } from "@/hooks/useOrgRole";
import { ProviderBudgetsTab } from "@/components/routing/ProviderBudgetsTab";
import { PolicyForm, type PolicyFormState } from "@/components/routing/PolicyForm";

const TASK_CLASSES = ["fast", "balanced", "reasoning", "verifier", "multimodal", "retrieval"];
const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))", "#888"];

type Insight = {
  task_class: string; model: string; provider: string;
  avg_cost_usd: number; avg_latency_ms: number;
  success_rate: number; fallback_rate: number;
  total_calls: number; total_cost_usd: number;
  efficiency_score: number;
};

type PolicyInsights = {
  summary: { total_calls: number; total_cost_usd: number; avg_latency_ms: number; unique_models: number; optimize_for?: string };
  insights: Insight[];
  recommendations: string[];
  auto_tuned: Array<{ task_class: string; new_model: string; new_provider: string; reason: string }>;
};

export default function ModelRouting() {
  const queryClient = useQueryClient();
  const { orgId } = useOrgRole();
  const [policyDialog, setPolicyDialog] = useState(false);
  const [optimizeFor, setOptimizeFor] = useState("balanced");
  const [policyForm, setPolicyForm] = useState<PolicyFormState>({
    task_class: "",
    preferred_model: "",
    fallback_chain: [],
    temperature_default: "0.7",
    max_tokens: "4096",
    sla_max_latency_ms: "",
  });
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);

  // Decisions state
  const [decisionsPage, setDecisionsPage] = useState(0);
  const [decisionFilter, setDecisionFilter] = useState({ provider: "", task_class: "" });
  const PAGE_SIZE = 20;

  const { data: policies = [] } = useQuery({
    queryKey: ["model-policies"],
    queryFn: async () => {
      const { data, error } = await supabase.from("model_policies").select("*").order("task_class");
      if (error) throw error;
      return data;
    },
  });

  const { data: insights, isLoading: insightsLoading, refetch: refetchInsights } = useQuery({
    queryKey: ["policy-insights", optimizeFor],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("policy-learner", { body: { days: 30, optimize_for: optimizeFor } });
      if (error) throw error;
      return data as PolicyInsights;
    },
    staleTime: 60000,
  });

  // Decisions query
  const { data: decisionsData, isLoading: decisionsLoading } = useQuery({
    queryKey: ["model-decisions", decisionsPage, decisionFilter],
    queryFn: async () => {
      let query = supabase
        .from("model_decisions")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(decisionsPage * PAGE_SIZE, (decisionsPage + 1) * PAGE_SIZE - 1);

      if (decisionFilter.provider) {
        query = query.ilike("provider_used", `%${decisionFilter.provider}%`);
      }
      if (decisionFilter.task_class) {
        query = query.eq("task_class", decisionFilter.task_class);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { decisions: data || [], total: count || 0 };
    },
  });

  const savePolicyMutation = useMutation({
    mutationFn: async (form: PolicyFormState & { id?: string }) => {
      const sla = parseInt(form.sla_max_latency_ms);
      const preferred = form.preferred_model;
      const [pProv, ...pRest] = preferred.includes("/") ? preferred.split("/") : ["", preferred];
      const pModel = pRest.join("/") || preferred;

      const firstFb = form.fallback_chain[0] || "";
      const [fbProv, ...fbRest] = firstFb.includes("/") ? firstFb.split("/") : ["", firstFb];
      const fbModel = fbRest.join("/") || firstFb;

      const record = {
        task_class: form.task_class,
        preferred_provider: pProv,
        preferred_model: pModel,
        fallback_provider: fbProv || null,
        fallback_model: fbModel || null,
        temperature_default: parseFloat(form.temperature_default) || 0.7,
        max_tokens: parseInt(form.max_tokens) || 4096,
        fallback_chain: form.fallback_chain,
        sla_max_latency_ms: isNaN(sla) ? null : sla,
        tier: form.task_class,
      };
      if (form.id) {
        const { error } = await supabase.from("model_policies").update(record).eq("id", form.id);
        if (error) throw error;
      } else {
        if (!orgId) throw new Error("Organização não encontrada");
        const { error } = await supabase.from("model_policies").insert({ ...record, org_id: orgId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["model-policies"] });
      toast.success("Política salva");
      setPolicyDialog(false);
      setEditingPolicyId(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const autoTuneMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("policy-learner", {
        body: { days: 30, auto_tune: true, optimize_for: optimizeFor },
      });
      if (error) throw error;
      return data as PolicyInsights;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["model-policies"] });
      queryClient.invalidateQueries({ queryKey: ["policy-insights"] });
      if (data.auto_tuned.length > 0) {
        toast.success(`${data.auto_tuned.length} política(s) otimizada(s) automaticamente`);
      } else {
        toast.info("Nenhuma otimização necessária. Políticas já estão ótimas");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function openEditPolicy(p: any) {
    const preferredId = p.preferred_provider && p.preferred_model
      ? `${p.preferred_provider}/${p.preferred_model}`.replace(/^\/+/, "")
      : p.preferred_model || "";
    let chain: string[] = Array.isArray(p.fallback_chain) ? p.fallback_chain.filter(Boolean) : [];
    if (chain.length === 0 && p.fallback_provider && p.fallback_model) {
      chain = [`${p.fallback_provider}/${p.fallback_model}`];
    }
    setPolicyForm({
      task_class: p.task_class,
      preferred_model: preferredId,
      fallback_chain: chain,
      temperature_default: String(p.temperature_default ?? 0.7),
      max_tokens: String(p.max_tokens ?? 4096),
      sla_max_latency_ms: p.sla_max_latency_ms ? String(p.sla_max_latency_ms) : "",
    });
    setEditingPolicyId(p.id);
    setPolicyDialog(true);
  }

  function openNewPolicy() {
    setPolicyForm({
      task_class: "",
      preferred_model: "",
      fallback_chain: [],
      temperature_default: "0.7",
      max_tokens: "4096",
      sla_max_latency_ms: "",
    });
    setEditingPolicyId(null);
    setPolicyDialog(true);
  }

  const costByModel = insights?.insights?.map((i) => ({ name: i.model, cost: i.total_cost_usd, calls: i.total_calls })) || [];
  const callsByTask = TASK_CLASSES.map((tc) => ({
    name: tc,
    calls: insights?.insights?.filter((i) => i.task_class === tc).reduce((s, i) => s + i.total_calls, 0) || 0,
  })).filter((d) => d.calls > 0);

  const efficiencyData = insights?.insights?.slice(0, 10).map(i => ({
    name: i.model.split("/").pop() || i.model,
    efficiency: i.efficiency_score,
    success: i.success_rate,
    cost: Math.round(i.avg_cost_usd * 1000000) / 1000,
  })) || [];

  const totalDecisionPages = Math.ceil((decisionsData?.total || 0) / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-headline">Roteamento de Modelos</h1>
          <p className="text-muted-foreground text-sm">Políticas, custos e otimização inteligente de IA</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={optimizeFor} onValueChange={setOptimizeFor}>
            <SelectTrigger className="w-[140px]">
              <Target className="w-4 h-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="balanced">Equilibrado</SelectItem>
              <SelectItem value="cost">Menor Custo</SelectItem>
              <SelectItem value="latency">Menor Latência</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => refetchInsights()} disabled={insightsLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${insightsLoading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button onClick={() => autoTuneMutation.mutate()} disabled={autoTuneMutation.isPending || !insights?.insights?.length}>
            <Wand2 className={`w-4 h-4 mr-2 ${autoTuneMutation.isPending ? "animate-spin" : ""}`} />
            Auto-Tune
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <Zap className="w-8 h-8 text-primary" />
          <div><p className="text-2xl font-bold">{insights?.summary?.total_calls ?? 0}</p><p className="text-xs text-muted-foreground">Chamadas (30d)</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <DollarSign className="w-8 h-8 text-green-500" />
          <div><p className="text-2xl font-bold">${insights?.summary?.total_cost_usd?.toFixed(2) ?? "0.00"}</p><p className="text-xs text-muted-foreground">Custo Total (30d)</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <Gauge className="w-8 h-8 text-orange-500" />
          <div><p className="text-2xl font-bold">{insights?.summary?.avg_latency_ms ?? 0}ms</p><p className="text-xs text-muted-foreground">Latência Média</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <Brain className="w-8 h-8 text-purple-500" />
          <div><p className="text-2xl font-bold">{insights?.summary?.unique_models ?? 0}</p><p className="text-xs text-muted-foreground">Modelos Usados</p></div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="analytics">
        <TabsList>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="efficiency">Eficiência</TabsTrigger>
          <TabsTrigger value="policies">Políticas ({policies.length})</TabsTrigger>
          <TabsTrigger value="budgets"><Wallet className="w-4 h-4 mr-1" /> Orçamentos</TabsTrigger>
          <TabsTrigger value="decisions">
            <FileText className="w-4 h-4 mr-1" /> Decisões
          </TabsTrigger>
          <TabsTrigger value="recommendations">Recomendações</TabsTrigger>
        </TabsList>

        <TabsContent value="budgets" className="space-y-4">
          <ProviderBudgetsTab orgId={orgId} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Custo por Modelo (USD)</CardTitle></CardHeader>
              <CardContent>
                {costByModel.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={costByModel.slice(0, 8)}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="cost" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-muted-foreground text-center py-8">Sem dados</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Chamadas por Task Class</CardTitle></CardHeader>
              <CardContent>
                {callsByTask.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={callsByTask} dataKey="calls" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, calls }) => `${name}: ${calls}`}>
                        {callsByTask.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-muted-foreground text-center py-8">Sem dados</p>}
              </CardContent>
            </Card>
          </div>

          {insights?.insights && insights.insights.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Detalhes por Modelo</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-muted-foreground border-b">
                      <th className="py-2">Task</th><th>Modelo</th><th>Chamadas</th><th>Custo Médio</th><th>Latência</th><th>Sucesso</th><th>Fallback</th><th>Eficiência</th>
                    </tr></thead>
                    <tbody>
                      {insights.insights.map((i, idx) => (
                        <tr key={idx} className="border-b border-muted/30">
                          <td className="py-2"><Badge variant="outline">{i.task_class}</Badge></td>
                          <td className="font-mono text-xs">{i.model}</td>
                          <td>{i.total_calls}</td>
                          <td>${i.avg_cost_usd.toFixed(6)}</td>
                          <td>{i.avg_latency_ms}ms</td>
                          <td><Badge className={i.success_rate >= 95 ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}>{i.success_rate}%</Badge></td>
                          <td>{i.fallback_rate}%</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Progress value={i.efficiency_score} className="w-16 h-2" />
                              <span className="text-xs font-mono">{i.efficiency_score}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="efficiency" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Score de Eficiência por Modelo</CardTitle></CardHeader>
            <CardContent>
              {efficiencyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={efficiencyData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="efficiency" name="Eficiência" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="success" name="Sucesso %" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-muted-foreground text-center py-8">Sem dados suficientes</p>}
            </CardContent>
          </Card>

          {insights?.auto_tuned && insights.auto_tuned.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Últimas Otimizações Auto-Tune</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {insights.auto_tuned.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Wand2 className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{t.task_class} → {t.new_model}</p>
                      <p className="text-xs text-muted-foreground">{t.reason}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="policies" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={policyDialog} onOpenChange={(o) => { if (!o) setEditingPolicyId(null); setPolicyDialog(o); }}>
              <DialogTrigger asChild>
                <Button onClick={openNewPolicy}><Plus className="w-4 h-4 mr-2" /> Nova Política</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingPolicyId ? "Editar Política" : "Nova Política de Modelo"}</DialogTitle></DialogHeader>
                <PolicyForm
                  value={policyForm}
                  onChange={setPolicyForm}
                  onSubmit={() => savePolicyMutation.mutate({ ...policyForm, id: editingPolicyId ?? undefined })}
                  isSaving={savePolicyMutation.isPending}
                  isEditing={!!editingPolicyId}
                />
              </DialogContent>
            </Dialog>
          </div>

          {policies.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Nenhuma política configurada. O gateway usará google/gemini-3-flash-preview por padrão.</CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {policies.map((p) => (
                <Card key={p.id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => openEditPolicy(p)}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge>{p.task_class}</Badge>
                        <span className="font-mono text-sm">{p.preferred_provider}/{p.preferred_model}</span>
                        {p.fallback_model && (
                          <span className="text-xs text-muted-foreground">→ {p.fallback_provider}/{p.fallback_model}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">T={p.temperature_default} · {p.max_tokens} tokens</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Decisions Tab (Explainability) ── */}
        <TabsContent value="decisions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Decisões do Gateway
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Provedor..."
                    className="w-32 h-8 text-xs"
                    value={decisionFilter.provider}
                    onChange={(e) => { setDecisionFilter(f => ({ ...f, provider: e.target.value })); setDecisionsPage(0); }}
                  />
                  <Select value={decisionFilter.task_class || "all"} onValueChange={(v) => { setDecisionFilter(f => ({ ...f, task_class: v === "all" ? "" : v })); setDecisionsPage(0); }}>
                    <SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Task" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {TASK_CLASSES.map(tc => <SelectItem key={tc} value={tc}>{tc}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {decisionsLoading ? (
                <p className="text-muted-foreground text-center py-8">Carregando...</p>
              ) : !decisionsData?.decisions.length ? (
                <p className="text-muted-foreground text-center py-8">Nenhuma decisão registrada.</p>
              ) : (
                <div className="space-y-2">
                  {decisionsData.decisions.map((d: any) => (
                    <Collapsible key={d.id}>
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-left">
                          <div className="flex items-center gap-3 min-w-0">
                            <Badge variant="outline" className="shrink-0">{d.task_class}</Badge>
                            <span className="font-mono text-xs truncate">{d.model_used}</span>
                            <span className="text-xs text-muted-foreground">{d.provider_used}</span>
                            {d.was_fallback && <Badge variant="secondary" className="text-xs shrink-0">fallback</Badge>}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-muted-foreground">{d.latency_ms}ms</span>
                            <span className="text-xs font-mono">${(d.estimated_cost_usd || 0).toFixed(6)}</span>
                            <span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString("pt-BR")}</span>
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-4 ml-4 border-l-2 border-primary/20 space-y-2">
                          {d.reason && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">Razão</p>
                              <p className="text-sm">{d.reason}</p>
                            </div>
                          )}
                          {d.alternatives_considered && Array.isArray(d.alternatives_considered) && d.alternatives_considered.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">Alternativas Consideradas</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {(d.alternatives_considered as Array<{ model: string; provider: string; skip_reason: string }>).map((alt, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {alt.provider}/{alt.model}: {alt.skip_reason}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span>Tokens: {d.input_tokens || 0} → {d.output_tokens || 0}</span>
                            {d.skill_name && <span>Skill: {d.skill_name}</span>}
                            <span>Sucesso: {d.success ? "✅" : "❌"}</span>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}

                  {/* Pagination */}
                  {totalDecisionPages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-xs text-muted-foreground">{decisionsData.total} decisões total</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={decisionsPage === 0} onClick={() => setDecisionsPage(p => p - 1)}>Anterior</Button>
                        <span className="text-xs text-muted-foreground self-center">{decisionsPage + 1}/{totalDecisionPages}</span>
                        <Button variant="outline" size="sm" disabled={decisionsPage >= totalDecisionPages - 1} onClick={() => setDecisionsPage(p => p + 1)}>Próxima</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4">
          {!insights?.recommendations || insights.recommendations.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              <Lightbulb className="w-8 h-8 mx-auto mb-3 text-yellow-500" />
              Sem recomendações no momento. Acumule mais dados de uso para receber sugestões de otimização.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {insights.recommendations.map((r, i) => (
                <Card key={i}>
                  <CardContent className="pt-4 flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                    <p className="text-sm">{r}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
